import type { FieldErrors, UseFormSetValue } from 'react-hook-form';
import type {
  FormField,
  FormFieldWidth,
  RadioGroupOption,
  BaseFormField,
  InputField,
  TextAreaField,
  PhoneField,
  CheckboxField,
  CheckboxGroupField,
  RadioGroupField,
  DropdownField,
  DatePickerField,
  DisclaimersField,
  FileUploadField,
} from './types';

/*
|--------------------------------------------------------------------------
| TYPES
|--------------------------------------------------------------------------
*/

/**
 * Data fields that have name and apiKey properties - excludes ButtonField, ButtonGroupField, ContentField, ReCaptchaField
 */
type DataFormField =
  | InputField
  | TextAreaField
  | PhoneField
  | CheckboxField
  | CheckboxGroupField
  | RadioGroupField
  | DropdownField
  | DatePickerField
  | DisclaimersField
  | FileUploadField;

type DropdownOptionItem = {
  label: string;
  value: string;
};

type ValidationType = 'success' | 'warning' | 'error';

type ValidationResponse =
  | {
      validationType: ValidationType;
      message: string;
    }
  | undefined;

type IsEmptyParams = {
  fieldType: {
    name: string;
    value?: unknown;
  };
  watchedValues: Record<string, unknown>;
};

type GetEffectiveWidthParams = {
  field: {
    width?: FormFieldWidth;
    widthWhen?: {
      width: FormFieldWidth;
      when: {
        name: string;
        value?: unknown;
      };
    };
  };
  watchedValues: Record<string, unknown>;
};

type PushToDataLayerParams = {
  id: string;
  step: string;
  parameters?: Record<string, unknown>;
};

type ObserveInputChangesParams = {
  formRef: React.RefObject<HTMLFormElement | HTMLDivElement | null>;
  setValue: UseFormSetValue<Record<string, unknown>>;
};

type SanitizeDataParams = {
  formFields: FormField[];
  data: Record<string, unknown>;
  locale?: string;
};

/*
|--------------------------------------------------------------------------
| getDefaultValues
|--------------------------------------------------------------------------
*/

export const getDefaultValues = (formFields: FormField[]): Record<string, unknown> => {
  const extractDefaults = (
    fields: FormField[],
    acc: Record<string, unknown> = {}
  ): Record<string, unknown> => {
    fields.forEach((field) => {
      if ('defaultValue' in field && field.defaultValue !== undefined) {
        acc[field.name] = field.defaultValue;
      }
      // Handle radio_group default option
      if (field.fieldType === 'radio_group' && 'options' in field && field.options) {
        const defaultOption = field.options.find((option: RadioGroupOption) => option.default);
        if (defaultOption) {
          acc[field.name] = defaultOption.value;
        }
      }
      // Handle nested children in group fields
      if (field.fieldType === 'group' && field.children && Array.isArray(field.children)) {
        extractDefaults(field.children as FormField[], acc);
      }
    });
    return acc;
  };
  return extractDefaults(formFields);
};

/*
|--------------------------------------------------------------------------
| validationPatterns
|--------------------------------------------------------------------------
*/

export const validationPatterns: Record<string, RegExp> = {
  email:
    /^(?=(.{1,64}@.{1,255}))([!#$%&'*+\-/=?^_`{|}~a-zA-Z0-9}]{1,64}(\.[!#$%&'*+\-/=?^_`{|}~a-zA-Z0-9]{0,}){0,})@((\[(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}\])|([a-zA-Z0-9-]{1,63}(\.[a-zA-Z0-9-]{2,63}){1,}))$/,
  password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#?!@$%^&*-]).{8,}$/,
  phone: /^\+?[\d\s\-().]{7,20}$/,
  first_name: /^[a-zA-Z\xC0-\uFFFF][a-zA-Z\xC0-\uFFFF\s'`-]*$/,
  last_name: /^[a-zA-Z\xC0-\uFFFF][a-zA-Z\xC0-\uFFFF\s'`-]*$/,
  city: /^(?=.*\p{L})[\p{L}\p{M}\d\s\-'.()/_]+$/u,
};

/*
|--------------------------------------------------------------------------
| dropdownOptions
|--------------------------------------------------------------------------
*/

export const dropdownOptions: Record<string, DropdownOptionItem[]> = {
  country: [
    { label: 'United States', value: 'US' },
    { label: 'Canada', value: 'CA' },
    { label: 'Australia', value: 'AU' },
    { label: 'Austria', value: 'AT' },
    { label: 'Belgium', value: 'BE' },
    { label: 'Brazil', value: 'BR' },
    { label: 'China', value: 'CN' },
    { label: 'Croatia', value: 'HR' },
    { label: 'Cyprus', value: 'CY' },
    { label: 'Czech Republic', value: 'CZ' },
    { label: 'Denmark', value: 'DK' },
    { label: 'Egypt', value: 'EG' },
    { label: 'Estonia', value: 'EE' },
    { label: 'Finland', value: 'FI' },
    { label: 'France', value: 'FR' },
    { label: 'Georgia', value: 'GE' },
    { label: 'Germany', value: 'DE' },
    { label: 'Greece', value: 'GR' },
    { label: 'Greenland', value: 'GL' },
    { label: 'Hong Kong', value: 'HK' },
    { label: 'Hungary', value: 'HU' },
    { label: 'Iceland', value: 'IS' },
    { label: 'India', value: 'IN' },
    { label: 'Indonesia', value: 'ID' },
    { label: 'Ireland', value: 'IE' },
    { label: 'Israel', value: 'IL' },
    { label: 'Italy', value: 'IT' },
    { label: 'Japan', value: 'JP' },
    { label: 'Kazakhstan', value: 'KZ' },
    { label: 'South Korea', value: 'KR' },
    { label: 'Latvia', value: 'LV' },
    { label: 'Liechtenstein', value: 'LI' },
    { label: 'Lithuania', value: 'LT' },
    { label: 'Luxembourg', value: 'LU' },
    { label: 'Macao', value: 'MO' },
    { label: 'Mexico', value: 'MX' },
    { label: 'Monaco', value: 'MC' },
    { label: 'Netherlands', value: 'NL' },
    { label: 'New Zealand', value: 'NZ' },
    { label: 'Norway', value: 'NO' },
    { label: 'Poland', value: 'PL' },
    { label: 'Portugal', value: 'PT' },
    { label: 'Qatar', value: 'QA' },
    { label: 'Romania', value: 'RO' },
    { label: 'Russia', value: 'RU' },
    { label: 'Saudi Arabia', value: 'SA' },
    { label: 'Singapore', value: 'SG' },
    { label: 'Spain', value: 'ES' },
    { label: 'Sweden', value: 'SE' },
    { label: 'Switzerland', value: 'CH' },
    { label: 'Taiwan', value: 'TW' },
    { label: 'TurapiKey', value: 'TR' },
    { label: 'Ukraine', value: 'UA' },
    { label: 'United Arab Emirates', value: 'AE' },
    { label: 'United Kingdom', value: 'GB' },
  ],
};

/*
|--------------------------------------------------------------------------
| getValidationResponse
|--------------------------------------------------------------------------
*/

export const getValidationResponse = ({
  field,
  errors,
}: {
  field: string;
  errors: FieldErrors;
}): ValidationResponse => {
  if (errors[field]) {
    return {
      validationType: 'error',
      message: (errors[field]?.message as string) ?? '',
    };
  }
  return undefined;
};

/*
|--------------------------------------------------------------------------
| isEmpty
|--------------------------------------------------------------------------
*/

export const isEmpty = ({ fieldType, watchedValues }: IsEmptyParams): boolean => {
  const value = watchedValues[fieldType.name];
  if (fieldType.value) {
    // Support array of values - show field if value matches any in the array
    if (Array.isArray(fieldType.value)) {
      return !fieldType.value.includes(value);
    }
    // Support watched value being an array (e.g., checkbox_group returns array of checked values)
    if (Array.isArray(value)) {
      return !value.includes(fieldType.value);
    }
    return fieldType.value !== value;
  }
  return !value || value === '' || value == null;
};

/*
|--------------------------------------------------------------------------
| hasValue
|--------------------------------------------------------------------------
*/

/**
 * Checks if a value is not empty (has a value)
 * @param value - The value to check
 * @returns True if the value is not empty
 */
export const hasValue = (value: unknown): boolean => {
  if (value === undefined || value === null || value === '') {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return true;
};

/*
|--------------------------------------------------------------------------
| getEffectiveWidth
|--------------------------------------------------------------------------
*/

export const getEffectiveWidth = ({
  field,
  watchedValues,
}: GetEffectiveWidthParams): FormFieldWidth => {
  const defaultWidth = field.width || 'full';

  if (!field.widthWhen) {
    return defaultWidth;
  }

  const { width, when } = field.widthWhen;
  const watchedValue = watchedValues[when.name];

  // Check if condition is met
  if (when.value !== undefined) {
    // Support array of values
    if (Array.isArray(when.value)) {
      if (when.value.includes(watchedValue)) {
        return width;
      }
    } else if (when.value === watchedValue) {
      return width;
    }
  } else if (watchedValue && watchedValue !== '') {
    // If no specific value, just check if field has a value
    return width;
  }

  return defaultWidth;
};

/*
|--------------------------------------------------------------------------
| pushToDataLayer
|--------------------------------------------------------------------------
*/

export const pushToDataLayer = ({ id, step, parameters }: PushToDataLayerParams): void => {
  window?.dataLayer?.push({
    event: 'process',
    process__name: id,
    process__step: step,
    ...(parameters ? { process__parameters: parameters } : {}),
  });
};

/*
|--------------------------------------------------------------------------
| observeInputChanges
|--------------------------------------------------------------------------
*/

export const observeInputChanges = ({
  formRef,
  setValue,
}: ObserveInputChangesParams): (() => void) | undefined => {
  if (!formRef?.current) return;

  // Exclude phone inputs (use onE164Change callback) and date pickers (use onChange callback with ISO format)
  const inputs = formRef.current.querySelectorAll(
    "input:not([type='hidden']):not([data-phone-input]):not([data-date-picker]):not([data-currency-input])"
  );

  const observer = new MutationObserver((mutationList) => {
    mutationList.forEach((element) => {
      const target = element?.target as HTMLInputElement;
      if (target?.value) {
        setValue(target?.name, target?.value, { shouldValidate: true });
      }
    });
  });

  inputs.forEach((input) => {
    observer.observe(input, { attributes: true, attributeFilter: ['value'] });
  });

  return () => observer.disconnect();
};

/*
|--------------------------------------------------------------------------
| flattenFormFields
|--------------------------------------------------------------------------
*/

/**
 * Flattens form fields by extracting children from group fields
 * @param formFields - Array of form field definitions
 * @returns Flattened array of all form fields
 */
export const flattenFormFields = (formFields: FormField[]): BaseFormField[] => {
  if (!formFields || !Array.isArray(formFields)) {
    return [];
  }
  return formFields.reduce<BaseFormField[]>((acc, field) => {
    if (field.fieldType === 'group' && field.children) {
      return [...acc, ...flattenFormFields(field.children as FormField[])];
    }
    return [...acc, field as BaseFormField];
  }, []);
};

/**
 * Type guard to check if a field is a data field (has name and apiKey)
 */
const isDataField = (field: BaseFormField): field is DataFormField => {
  return 'name' in field && 'apiKey' in field && typeof field.name === 'string';
};

/*
|--------------------------------------------------------------------------
| sanitizeData
|--------------------------------------------------------------------------
*/

export const sanitizeData = ({
  formFields,
  data,
  locale,
}: SanitizeDataParams): Record<string, unknown> => {
  // Flatten form fields to handle nested children in groups
  const flatFields = flattenFormFields(formFields);
  // Filter to only data fields (fields with name and apiKey)
  const dataFields = flatFields.filter(isDataField);

  return dataFields.reduce<Record<string, unknown>>((acc, field) => {
    // For checkbox_group and multi-select dropdowns, always include as array (even if empty)
    const isArrayField =
      field.fieldType === 'checkbox_group' ||
      (field.fieldType === 'dropdown' && 'multiSelect' in field && field.multiSelect);

    if (isArrayField) {
      let fieldValue = data[field.name];
      if (!fieldValue || fieldValue === '') {
        fieldValue = [];
      } else if (typeof fieldValue === 'string') {
        // Convert comma-separated string back to array (from hidden input)
        fieldValue = fieldValue.split(',').filter(Boolean);
      } else if (!Array.isArray(fieldValue)) {
        // Wrap single values in array
        fieldValue = [fieldValue];
      }
      acc[field.apiKey] = fieldValue;
      return acc;
    }

    if (data[field.name] === null || data[field.name] === undefined) {
      return acc;
    }

    if ('dependency' in field && field.dependency) {
      const { name, value } = field.dependency;
      const dependentValue = data[name];

      // If value is defined, check for a specific match
      if (value !== undefined) {
        // Handle dependency value being an array (e.g., value: ['cash', 'third_party_finance'])
        if (Array.isArray(value)) {
          // Check if the dependent form value matches any value in the array
          if (Array.isArray(dependentValue)) {
            // Both are arrays - check if any dependent value is in the allowed values
            if (!dependentValue.some((v) => value.includes(v))) {
              return acc; // Skip adding the field if condition is not met
            }
          } else if (!value.includes(dependentValue)) {
            return acc; // Skip adding the field if condition is not met
          }
        }
        // Handle dependent form value being an array (e.g., checkbox_group returns array of checked values)
        else if (Array.isArray(dependentValue)) {
          if (!dependentValue.includes(value)) {
            return acc; // Skip adding the field if condition is not met
          }
        } else if (dependentValue !== value) {
          return acc; // Skip adding the field if condition is not met
        }
      } else {
        // If value is undefined, just check if the dependent field exists
        if (!dependentValue) {
          return acc; // Skip adding the field if the dependent field is missing
        }
      }
    }

    // For legal consent requirements
    switch (field.name) {
      case 'email':
        // For US locale, email consent checkbox and language is not displayed.
        // When a form includes an email input field, we simply set newsLetterOptOut to false and set consent language to empty string.
        if (locale === 'en') {
          acc.newsLetterOptOut = false;
          acc.consentLanguageEmail = '';
        }
        break;
      case 'newsLetterOptOut':
        // Adds the email consent language from the field's label to the payload.
        acc.consentLanguageEmail = 'label' in field ? field.label || '' : '';
        break;
      case 'callConsentOptIn':
        // Adds the phone consent language from the field's label to the payload.
        acc.consentLanguagePhone = 'label' in field ? field.label || '' : '';
        break;
    }

    const fieldValue = data[field.name];

    acc[field.apiKey] = field.name === 'newsLetterOptOut' ? !fieldValue : fieldValue;
    return acc;
  }, {});
};
